import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SnippetWithRating, SnippetsSearchRequest } from '@kemu-io/kemu-types';

// Extended snippet type with UI-specific properties
export interface UISnippet extends Omit<SnippetWithRating, 'category'> {
  category: {
    name: string;
    color: string;
  };
}

export type SnippetsView = 'list' | 'detail';

export interface SnippetsState {
  // Drawer state
  isDrawerVisible: boolean;
  currentView: SnippetsView;
  selectedSnippet: UISnippet | null;
  
  // Search and filter state
  searchConfig: SnippetsSearchRequest;
  searchResults: UISnippet[];
  totalResults: number;
  hasMore: boolean;
  isLoading: boolean;
  error: string | null;
}

const initialState: SnippetsState = {
  isDrawerVisible: false,
  currentView: 'list',
  selectedSnippet: null,
  
  searchConfig: {
    query: '',
    category: '',
    featured: false,
    recommended: false,
    sortBy: 'most_liked',
    visibility: 'public',
    page: 1,
    limit: 20,
  },
  searchResults: [],
  totalResults: 0,
  hasMore: false,
  isLoading: false,
  error: null,
};

export const snippetsSlice = createSlice({
  name: 'snippets',
  initialState,
  reducers: {
    /**
     * Opens the snippets drawer
     */
    openSnippetsDrawer: (state) => {
      state.isDrawerVisible = true;
      state.currentView = 'list';
      state.selectedSnippet = null;
      state.error = null;
    },

    /**
     * Opens the snippets drawer with a specific snippet selected
     */
    toggleSnippetsDrawer: (state) => {
      state.isDrawerVisible = !state.isDrawerVisible;
      state.currentView = 'list';
      state.selectedSnippet = null;
      state.error = null;
    },

    /**
     * Closes the snippets drawer and resets state
     */
    closeSnippetsDrawer: (state) => {
      state.isDrawerVisible = false;
      state.currentView = 'list';
      state.selectedSnippet = null;
      state.searchResults = [];
      state.totalResults = 0;
      state.hasMore = false;
      state.isLoading = false;
      state.error = null;
    },

    /**
     * Sets the current view (list or detail)
     */
    setCurrentView: (state, action: PayloadAction<SnippetsView>) => {
      state.currentView = action.payload;
    },

    /**
     * Sets the selected snippet for detail view
     */
    setSelectedSnippet: (state, action: PayloadAction<UISnippet | null>) => {
      state.selectedSnippet = action.payload;
      if (action.payload) {
        state.currentView = 'detail';
      }
    },

    /**
     * Updates the search configuration
     */
    updateSearchConfig: (state, action: PayloadAction<Partial<SnippetsSearchRequest>>) => {
      state.searchConfig = {
        ...state.searchConfig,
        ...action.payload,
      };
      // Reset pagination when search config changes
      if (action.payload.query !== undefined || 
          action.payload.category !== undefined || 
          action.payload.featured !== undefined || 
          action.payload.recommended !== undefined || 
          action.payload.sortBy !== undefined) {
        state.searchConfig.page = 1;
      }
    },

    /**
     * Sets the search results
     */
    setSearchResults: (state, action: PayloadAction<{
      snippets: UISnippet[];
      total: number;
      hasMore: boolean;
      append?: boolean;
    }>) => {
      const { snippets, total, hasMore, append = false } = action.payload;
      
      if (append) {
        state.searchResults = [...state.searchResults, ...snippets];
      } else {
        state.searchResults = snippets;
      }
      
      state.totalResults = total;
      state.hasMore = hasMore;
    },

    /**
     * Sets the loading state
     */
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    /**
     * Sets the error state
     */
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    /**
     * Updates a snippet in the search results (e.g., after rating)
     */
    updateSnippetInResults: (state, action: PayloadAction<{
      snippetId: string;
      updates: Partial<UISnippet>;
    }>) => {
      const { snippetId, updates } = action.payload;
      const index = state.searchResults.findIndex(snippet => snippet.id === snippetId);
      
      if (index !== -1) {
        state.searchResults[index] = {
          ...state.searchResults[index],
          ...updates,
        };
      }

      // Also update selected snippet if it's the same one
      if (state.selectedSnippet?.id === snippetId) {
        state.selectedSnippet = {
          ...state.selectedSnippet,
          ...updates,
        };
      }

      // If the updated snippet has a different visibility, or filters, remove it from the search result if not matching config
      if ((updates.visibility !== undefined && state.searchConfig.visibility !== updates.visibility) || 
          (updates.category !== undefined && state.searchConfig.category !== updates.category.name)) {
        // unless we are in the private section
        if (state.searchConfig.visibility === 'private') { return; }

        state.searchResults = state.searchResults.filter(snippet => snippet.id !== snippetId);
      }
    },

    /**
     * Adds a snippet to the search results
     */
    addSnippetToResults: (state, action: PayloadAction<UISnippet>) => {
      state.searchResults = [...state.searchResults, action.payload];
    },


    /**
     * Clears search results
     */
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.totalResults = 0;
      state.hasMore = false;
    },
  },
});

export const {
  openSnippetsDrawer,
  toggleSnippetsDrawer,
  closeSnippetsDrawer,
  setCurrentView,
  setSelectedSnippet,
  updateSearchConfig,
  setSearchResults,
  setLoading,
  setError,
  updateSnippetInResults,
  clearSearchResults,
  addSnippetToResults,
} = snippetsSlice.actions;

// Selectors
export const selectSelectedSnippet = (state: { snippets: SnippetsState }): UISnippet | null => 
  state.snippets.selectedSnippet;

export default snippetsSlice.reducer;
